<template>
  <div class="empty-state-centered mx-1">
    <component :is="imageFile" :color="imageColorComputed" class="mb-3" aria-labelledby="desc">
      <desc>{{ altFinal }}</desc>
    </component>
    <div class="mb-2">
      <span v-if="boldMessage" class="empty-state-message-bold">
        {{ boldMessage }}
      </span>
      <span v-if="message" class="empty-state-message">
        {{ message }}
      </span>
      <ActionButtons :actions="actions" class="mt-3" />
    </div>
    <div v-if="subMessage" class="empty-state-submessage">
      {{ subMessage }}
    </div>
    <div v-for="(h, i) in help" :key="i" class="learn-more" :class="{ disabled: h.disabled }">
      <a href="#" @click="execute(h)">{{ h.name }}</a>
    </div>
  </div>
</template>

<script>
import ActionButtons from "@/components/display/ActionButtons";
import { computed } from "@vue/composition-api";
import { useTheme } from "@/utils/index";

export default {
  name: "EmptyState",
  components: {
    ActionButtons,
  },
  props: {
    actions: Array,
    help: Array,
    image: {
      type: String,
      default: "default",
    },
    imageAlt: String,
    imageColor: String,
    boldMessage: String,
    message: String,
    subMessage: String,
  },
  setup(props) {
    const execute = (action) => action.action();
    const { theme } = useTheme();
    const imageList = {
      announcements: () =>
        import(
          /* webpackChunkName: "emptystate" */ "@/components/display/empty-state/svg-components/EmptyStateAnnouncements"
        ),
      backups: () =>
        import(
          /* webpackChunkName: "emptystate" */ "@/components/display/empty-state/svg-components/EmptyStateBackups"
        ),
      "credit-cards": () =>
        import(
          /* webpackChunkName: "emptystate" */ "@/components/display/empty-state/svg-components/EmptyStatePayments"
        ),
      credits: () =>
        import(
          /* webpackChunkName: "emptystate" */ "@/components/display/empty-state/svg-components/EmptyStateCredits"
        ),
      default: () =>
        import(
          /* webpackChunkName: "emptystate" */ "@/components/display/empty-state/svg-components/EmptyStateDefault"
        ),
      environments: () =>
        import(
          /* webpackChunkName: "emptystate" */ "@/components/display/empty-state/svg-components/EmptyStateEnvironments"
        ),
      error: () =>
        import(
          /* webpackChunkName: "emptystate" */ "@/components/display/empty-state/svg-components/EmptyStateError"
        ),
      invoices: () =>
        import(
          /* webpackChunkName: "emptystate" */ "@/components/display/empty-state/svg-components/EmptyStateInvoices"
        ),
      "not-authorized": () =>
        import(
          /* webpackChunkName: "emptystate" */ "@/components/display/empty-state/svg-components/EmptyStateNotAuthorized"
        ),
      orders: () =>
        import(
          /* webpackChunkName: "emptystate" */ "@/components/display/empty-state/svg-components/EmptyStateOrders"
        ),
      "partner-links": () =>
        import(
          /* webpackChunkName: "emptystate" */ "@/components/display/empty-state/svg-components/EmptyStatePartnerLinks"
        ),
      payments: () =>
        import(
          /* webpackChunkName: "emptystate" */ "@/components/display/empty-state/svg-components/EmptyStatePayments"
        ),
      plans: () =>
        import(
          /* webpackChunkName: "emptystate" */ "@/components/display/empty-state/svg-components/EmptyStatePlans"
        ),
      "pointer-domains": () =>
        import(
          /* webpackChunkName: "emptystate" */ "@/components/display/empty-state/svg-components/EmptyStatePointerDomains"
        ),
      referrals: () =>
        import(
          /* webpackChunkName: "emptystate" */ "@/components/display/empty-state/svg-components/EmptyStateReferrals"
        ),
      ssl: () =>
        import(
          /* webpackChunkName: "emptystate" */ "@/components/display/empty-state/svg-components/EmptyStateSsl"
        ),
      stencils: () =>
        import(
          /* webpackChunkName: "emptystate" */ "@/components/display/empty-state/svg-components/EmptyStateStencils"
        ),
      tasks: () =>
        import(
          /* webpackChunkName: "emptystate" */ "@/components/display/empty-state/svg-components/EmptyStateTasks"
        ),
      tickets: () =>
        import(
          /* webpackChunkName: "emptystate" */ "@/components/display/empty-state/svg-components/EmptyStateTickets"
        ),
      "visual-comparison": () =>
        import(
          /* webpackChunkName: "emptystate" */ "@/components/display/empty-state/svg-components/EmptyStateVisualComparison"
        ),
    };

    const imageFile = computed(() => {
      return imageList[props.image] || imageList["default"];
    });

    const imageColorComputed = computed(() => {
      return theme.value?.iconColors?.[props.imageColor] || theme.value?.iconColors?.omicron;
    });

    const altFinal = computed(() => {
      return props.imageAlt || props.image.charAt(0).toUpperCase() + props.image.slice(1);
    });

    return {
      execute,
      imageFile,
      imageColorComputed,
      altFinal,
    };
  },
};
</script>

<style lang="scss" scoped>
.empty-state-centered {
  text-align: center;
  margin-top: 2.5rem;
  margin-bottom: 2.125rem;

  .empty-state-message-bold,
  .empty-state-message {
    font-size: 1.5rem;
  }
  .empty-state-message-bold {
    font-weight: bold;
  }
  .empty-state-message {
    font-weight: normal;
  }
  .empty-state-submessage {
    font-size: 0.875rem;
  }
  .learn-more {
    font-size: 0.875rem;
    margin-top: 1rem;
  }
}
</style>
